const mapboxgl = require("mapbox-gl");
const segmentInsert = require("@mapbox/segmentio-insert");
const tBbox = require("@turf/bbox");
const tIntersect = require("@turf/intersect");
const tBuffer = require("@turf/buffer");
const tSimplify = require("@turf/simplify");
const chroma = require("chroma-js");
const _ = require("lodash");
const metadataTable = require("./data/mapbox-boundaries-pos3-v3_1.json");
const zipToRegionTable = require("./data/zipcodes.json");
const salesTable = require("./data/accountlist.json");

mapboxgl.accessToken = "pk.eyJ1IjoibGFicy1zYW5kYm94IiwiYSI6ImNrMTZuanRqNjE3cWozYm54am5wemViZjEifQ.W_vde3dIbd4u9EwuOgmsgw";
const analytics = segmentInsert("fl0c8p240n");
analytics.track("Demo Viewed", { name: "sales-planning" });

// global variables
const d = document;
let selector;
const allFeatProps = [];
let clicked = [];

// lookup of feature_id to {"region": <region>, "name": <name>} from zipcodes.json
const region_lookup = new Map();

// lookup of feature_id to total sales from accountlist.json
const total_sales_lookup = new Map();

// lookup of feature_id to count of zips from accountlist.json
const zipcode_count_lookup = new Map();

let postal3_count = [];

// initialize map
const map = new mapboxgl.Map({
  container: "map",
  style: "mapbox://styles/mapbox/light-v9",
  center: [-99, 39],
  zoom: 4
});
map.on("load", () => {
  map.doubleClickZoom.disable();

  // add boundaries tileset
  map.addSource("postal-3-source", {
    type: "vector",
    url: "mapbox://mapbox.boundaries-pos3-v3"
  });

  // create listeners for click and hover events
  /* eslint-disable */
  const LassoDraw = MapboxDraw.modes.draw_polygon;
  const draw = new MapboxDraw({
    displayControlsDefault: false,
    controls: {
      polygon: true
    }
  });
  /* eslint-enable */
  LassoDraw.onSetup_original = function () {
    const polygon = this.newFeature({
      type: "Feature",
      properties: {},
      geometry: {
        type: "Polygon",
        coordinates: [
          []
        ]
      }
    });

    this.addFeature(polygon);

    this.clearSelectedFeatures();
    this.updateUIClasses({
      mouse: "add"
    });
    this.activateUIButton("Polygon");
    this.setActionableState({
      trash: true
    });

    return {
      polygon,
      currentVertexPosition: 0,
      dragMoving: false,
      toggled: false
    };
  };

  LassoDraw.onClick = function (state) {
    state.toggled = !state.toggled;

    if (!state.toggled) {
      const factor = Math.min(Math.floor(this.map.getZoom()), 4);
      let tolerance = (3 / ((this.map.getZoom() - factor) * 150)) - 0.001; // https://www.desmos.com/calculator/b3zi8jqskw

      if (tolerance < 0 || !(isFinite(tolerance))) {
        // Tolerance cannot be negative
        tolerance = 0;
      }
      tSimplify(state.polygon, {
        tolerance: tolerance
      });
      this.fireUpdate();
      this.changeMode("simple_select", {
        featureIds: [state.polygon.id]
      });
    }

  };

  LassoDraw.onMouseMove = LassoDraw.onTouchMove = function (state, e) {
    state.dragMoving = true;
    if (state.toggled) {
      this.updateUIClasses({
        mouse: "add"
      });
      state.polygon.updateCoordinate(`0.${state.currentVertexPosition}`, e.lngLat.lng, e.lngLat.lat);
      state.currentVertexPosition++;
      state.polygon.updateCoordinate(`0.${state.currentVertexPosition}`, e.lngLat.lng, e.lngLat.lat);
    }
  };

  LassoDraw.fireUpdate = function () {
    this.map.fire("draw.update", {
      action: "move",
      features: this.getSelected().map(f => f.toGeoJSON())
    });
  };

  map.addControl(draw);

  function initmap() {
    function updateArea() {
      const data = draw.getAll();
      if (data.features.length > 0) {
        const polygon = data.features[0];
        const bbox = tBbox(polygon);
        const southWest = [bbox[0], bbox[1]];
        const northEast = [bbox[2], bbox[3]];
        const southWestPixel = map.project(southWest);
        const northEastPixel = map.project(northEast);
        const features = map.queryRenderedFeatures(
          [southWestPixel, northEastPixel], {
            layers: ["postal-3-layer"]
          }
        );
        const filter = features.reduce(
          (memo, feature) => {
            if (!(undefined === tIntersect(tBuffer(feature, 0), tBuffer(polygon, 0)))) {
              // only add the property, if the feature intersects with the polygon drawn by the user
              memo.push(feature.id);
            }
            return memo;
          }, []
        );

        filter.forEach(k => {
          region_lookup[k].region = document.getElementById("selector").value;
        });
        selector = document.getElementById("selector").value;
        colorSelection(selector);
        draw.deleteAll();
      }
    }
    function zoomToSelected () {
      let exists = false;
      let region_id = document.getElementById("selector").value;
      if (region_id === undefined) {
        region_id = "North";
      }

      for (const key in region_lookup) {

        if (region_lookup[key].region === region_id) {
          exists = true;
        }
      }
      if (exists) {
        let bounds0 = 0;
        let bounds1 = 100;
        let bounds2 = -100;
        let bounds3 = 0;

        // iterate over every pos-3 feature in the US
        for (const key in region_lookup) {
          const name = region_lookup[key].name;

          // if the feature is in the currently displayed region,
          // find its bounds in the metadata table. Use this to progressively
          // build the bounding box to zoom to
          if (region_lookup[key].region === region_id) {
            if (metadataTable.pos3.data.all[name].bounds[0] < bounds0) {
              bounds0 = metadataTable.pos3.data.all[name].bounds[0];
            }
            if (metadataTable.pos3.data.all[name].bounds[1] < bounds1) {
              bounds1 = metadataTable.pos3.data.all[name].bounds[1];
            }
            if (metadataTable.pos3.data.all[name].bounds[2] > bounds2) {
              bounds2 = metadataTable.pos3.data.all[name].bounds[2];
            }
            if (metadataTable.pos3.data.all[name].bounds[3] > bounds3) {
              bounds3 = metadataTable.pos3.data.all[name].bounds[3];
            }
            clicked.push(key);
          }

        }
        map.fitBounds([
          [
            bounds0, bounds1
          ],
          [
            bounds2,
            bounds3
          ]
        ], {
          padding: {
            top: 0,
            bottom: 150,
            left: 500,
            right: 0
          }
        });
      }
      colorSelection(document.getElementById("selector").value);
    }

    // helper to change colors of zips from dropdown selection
    function colorSelection(region_id) {
      clicked = [];
      for (const key in region_lookup) {
        if (region_lookup[key].region === region_id) {
          map.setFeatureState({
            source: "postal-3-source",
            sourceLayer: "boundaries_postal_3",
            id: key
          }, {
            "clicked": 1
          });
          clicked.push(key);
        } else {
          map.setFeatureState({
            source: "postal-3-source",
            sourceLayer: "boundaries_postal_3",
            id: key
          }, {
            "clicked": 0
          });

        }

      }

      aggregateData(region_id);
    }

    // helper to aggregate data
    function aggregateData(region_id) {
      let sales = 0;
      let numAccounts = 0;
      const salesAg = [];
      let agSales = 0;

      for (const key in region_lookup) {
        if (region_lookup[key].region === region_id) {
          salesAg.push(key);
        }
      }
      salesAg.forEach(zip => {
        sales = ((sales + total_sales_lookup[zip]));
        agSales = (sales / 1000000).toFixed(2);
        numAccounts = parseInt(numAccounts + postal3_count[zip]);
      });
      document.getElementById("totalsales").innerHTML = `$${agSales} M`;
      document.getElementById("totalaccounts").innerHTML = `${numAccounts}`;
      updateTable();
    }

    function updateTable () {

      const territoryArray = [];

      for (const key in region_lookup) {
        let found = false;
        territoryArray.forEach(k => {
          if (k === region_lookup[key].region) {
            found = true;
          }
        });
        if (!found) {
          territoryArray.push(region_lookup[key].region);
        }
      }
      territoryArray.push("N/A");

      territoryArray.forEach(k => {
        let sales = 0;
        let numAccounts = 0;
        let agSales = 0;
        for (const key in region_lookup) {
          if (k === region_lookup[key].region) {
            sales = ((sales + total_sales_lookup[key]));
            agSales = (sales / 1000000).toFixed(2);
            numAccounts = parseInt(numAccounts + postal3_count[key]);
          }
        }
        const a_id = `a_${k}`;
        const s_id = `s_${k}`;
        document.getElementById(a_id).innerHTML = `${numAccounts}`;
        document.getElementById(s_id).innerHTML = `$${agSales}M`;
      });

    }

    function clickevent(e) {
      const features = map.queryRenderedFeatures(e.point, {
        layers: ["postal-3-layer"]
      });
      if (features.length) {
        let found;
        clicked.forEach(f => {
          if (parseInt(f) === features[0].id) {
            found = true;
          }
        });

        if (found) {

          region_lookup[features[0].id].region = "N/A";
          map.setFeatureState({
            source: "postal-3-source",
            sourceLayer: "boundaries_postal_3",
            id: features[0].id
          }, {
            "clicked": 0
          });
          colorSelection(document.getElementById("selector").value);
        } else {
          region_lookup[features[0].id].region = document.getElementById("selector").value;
          for (const key in region_lookup) {
            if (region_lookup[key].region === document.getElementById("selector").value) {
              map.setFeatureState({
                source: "postal-3-source",
                sourceLayer: "boundaries_postal_3",
                id: features[0].id
              }, {
                "clicked": 1
              });
            }
          }
          colorSelection(document.getElementById("selector").value);
        }
      }
      aggregateData(document.getElementById("selector").value);

    }

    // Create Lookup Tables
    zipToRegionTable.forEach(f => {
      region_lookup[f.feature_id] = { "region": f.region, "name": f.name };
    });

    // process our sales table, will ultimately create a feature_id: total sales mapping
    salesTable.forEach(f => {
      const newData = {
        feature_id: f.feature_id,
        Sales: parseInt(f.PYSales)
      };
      allFeatProps.push(newData);
    });

    // Use lodash to loop through all the accounts and group Sales by Zip code
    const postal3_groups = _(allFeatProps).groupBy("feature_id").map((v, k) => ({
      [k]: _.sumBy(v, "Sales")
    })).value();

    // Use lodash to loop through all accounts and count the number of accounts per zipcode
    postal3_count = _(allFeatProps).countBy("feature_id").value();

    for (const k in postal3_count) {
      zipcode_count_lookup[Object.keys(k)[0]] = Object.values(k)[0];
    }

    const postal3poly_domain = [];
    postal3_groups.forEach(k => {
      postal3poly_domain.push(Object.values(k)[0]);
    });

    // build filter expressions to show only feature_ids in our postal3_groups map
    const postal3_filter = ["in", ["id"], ["literal", []]];

    // build color expression based on our range of values in the data
    const postal3_color_expression = ["match", ["id"]];
    const postal3_color_groups = new Map();
    const postal3_domain = chroma.limits(postal3poly_domain, "k", 10).slice(0, 9);
    const postal3_scale = chroma.scale(chroma.brewer.YlGnBu).domain(postal3_domain).mode("lab");

    postal3_groups.forEach(k => {
      const postal3_id = parseInt(Object.keys(k)[0]);
      postal3_filter[2][1].push(postal3_id);
      total_sales_lookup[postal3_id] = Math.round(Object.values(k)[0]);

      const color = postal3_scale(Object.values(k)[0]).hex();
      if ( !postal3_color_groups[color] ) {
        postal3_color_groups[color] = [];
      }
      postal3_color_groups[color].push(postal3_id);
    });

    for (const k in postal3_color_groups) {
      postal3_color_expression.push(postal3_color_groups[k]);
      postal3_color_expression.push(k);
    }

    // add fallback default color
    postal3_color_expression.push("grey");

    map.addLayer({
      id: "postal-3-layer",
      type: "fill",
      source: "postal-3-source",
      "source-layer": "boundaries_postal_3",
      "paint": {
        "fill-color": postal3_color_expression,
        "fill-opacity": 0.7
      },
      "filter": postal3_filter
    }, "waterway-label");

    map.addLayer({
      id: "postal-3-line",
      type: "line",
      source: "postal-3-source",
      "source-layer": "boundaries_postal_3",
      "paint": {
        "line-color": "grey",
        "line-width": {
          "stops": [
            [4, 0.1],
            [12, 1]
          ],
          "base": 1.2
        }

      },
      "filter": postal3_filter
    }, "waterway-label");

    map.addLayer({
      id: "postal-3-click",
      type: "fill",
      source: "postal-3-source",
      "source-layer": "boundaries_postal_3",
      "paint": {
        "fill-opacity": 0.7,
        "fill-color": ["case", ["==", ["feature-state", "clicked"], 1], "#FFA500", "rgba(0,0,0,0)"]
      }
    }, "waterway-label");
    map.addLayer({
      "id": "postal-3-highlight",
      "type": "line",
      "source": "postal-3-source",
      "source-layer": "boundaries_postal_3",
      "paint": {
        "line-color": ["case", ["==", ["feature-state", "hover"], 1], "#696969", "rgba(0,0,0,0)"],
        "line-width": 5
      }
    }, "waterway-label");

    let hoverIds = [];

    const inArray = function (array, element) {
      array.forEach(item => {
        if (item === element) {
          return true;
        } else {
          return false;
        }
      });
    };
    const popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false
    });

    const move = function (e) {
      const features = map.queryRenderedFeatures(e.point, {
        layers: ["postal-3-layer"]
      });

      if (features.length) {
        map.getCanvas().style.cursor = "pointer";
        features.forEach(e => {
          if (!inArray(hoverIds, e.id)) {
            hoverIds.push(e.id);
          }
          map.setFeatureState({
            source: "postal-3-source",
            sourceLayer: "boundaries_postal_3",
            id: e.id
          }, {
            "hover": 1
          });
        });

        const postCode = region_lookup[features[0].id].name;

        const outerDiv = d.createElement("div");
        const title = d.createElement("p");
        outerDiv.setAttribute("class", "flex-parent-inline flex-parent flex-parent--column color-gray-dark");
        const innerLi3 = d.createElement("li");
        const innerLi = d.createElement("li");
        const innerLi2 = d.createElement("li");
        const innerLi4 = d.createElement("li");
        const titleText = d.createTextNode("Detailed Info");
        const innerLiText3 = d.createTextNode(`Post Code: ${postCode}`);
        const innerLiText = d.createTextNode(`Total Sales: $${(parseInt(total_sales_lookup[features[0].id]) / 1000000).toFixed(2)} M`);
        const innerLiText2 = d.createTextNode(`Total Accounts: ${zipcode_count_lookup[features[0].id]}`);
        const innerLiText4 = d.createTextNode(`Sales Territory: ${region_lookup[features[0].id].region}`);
        innerLi3.appendChild(innerLiText3);
        innerLi.appendChild(innerLiText);
        innerLi2.appendChild(innerLiText2);
        innerLi4.appendChild(innerLiText4);
        title.appendChild(titleText);
        outerDiv.appendChild(title);
        outerDiv.appendChild(innerLi3);
        outerDiv.appendChild(innerLi);
        outerDiv.appendChild(innerLi2);
        outerDiv.appendChild(innerLi4);
        e.point.y = e.point.y - 10;
        popup.setLngLat(map.unproject(e.point))
          .setDOMContent(outerDiv)
          .addTo(map);
      }


      if (!features.length) {
        map.getCanvas().style.cursor = "";
        hoverIds.forEach(id => {
          map.setFeatureState({
            source: "postal-3-source",
            sourceLayer: "boundaries_postal_3",
            id: id
          }, {
            "hover": 0
          });
        });
        popup.remove();
        hoverIds = [];
      } else if (hoverIds.length && hoverIds[0] !== features[0].id) {
        hoverIds.forEach(id => {
          map.setFeatureState({
            source: "postal-3-source",
            sourceLayer: "boundaries_postal_3",
            id: id
          }, {
            "hover": 0
          });
        });
        popup.remove();
        hoverIds = [];
      }
    };
    // Add event listener to the dropdown to update colors and recalculate data
    document.getElementById("selector").addEventListener("change", () => {
      selector = document.getElementById("selector").value;
      zoomToSelected();

    });

    map.on("mousemove", move);
    map.on("click", clickevent);
    map.on("draw.create", updateArea);
    map.on("draw.delete", updateArea);
    map.on("draw.update", updateArea);
    zoomToSelected();
  }
  initmap();
});
